/**
 * FASE 22.5 / 23.9 — PROACTIVE INSIGHTS
 *
 * Generates pastoral insight strings from followup items.
 * Warm, contemplative tone — not corporate or task-list-like.
 * Max 2 insights emitted at once to avoid overwhelming.
 */

import { computeFollowupItems } from "./followupEngine";
import { getPriorityWorkflows } from "./workflowPriorities";
import { getWorkflowDefinition } from "./workflowSchemas";

/**
 * @typedef {Object} PastoralInsight
 * @property {"reminder"|"encouragement"|"alert"} type
 * @property {string} text
 * @property {string|null} workflowId
 * @property {number} priority — 0 (low) to 3 (critical)
 */

/* ─── Insight templates — human, pastoral, varied ─────────────── */

const OVERDUE_TEMPLATES = [
  (label) => `${label}. ¿Ha habido oportunidad de retomar el contacto?`,
  (label) => `Hace tiempo que no hay avances en ${label}. A veces una llamada corta puede hacer mucho.`,
  (label) => `${label} está esperando atención. No hay prisa, pero vale recordarlo.`,
  (label) => `¿Cómo está yendo ${label}? Han pasado varios días desde la última actividad.`,
];

const STAGNANT_TEMPLATES = [
  (label) => `${label} todavía está en sus primeros pasos. ¿Algo que te pueda ayudar a avanzar?`,
  (label) => `${label} lleva un tiempo sin moverse. A veces lo que falta es solo un pequeño paso concreto.`,
  (label) => `¿Hay algo que esté dificultando avanzar en ${label}?`,
];

const NEAR_LIMIT_TEMPLATES = [
  (label) => `Pronto puede ser buen momento para retomar ${label}.`,
  (label) => `${label} necesitará atención en los próximos días.`,
];

const ENCOURAGEMENT_TEMPLATES = [
  (label) => `Tienes en marcha ${label}. ¿Cómo va ese proceso?`,
  (label) => `${label} está activo. ¿Hay algo en lo que pueda ayudarte hoy?`,
  (label) => `Recuerdo que tienes ${label} en curso. ¿Todo bien?`,
];

function pickTemplate(templates, seed) {
  return templates[seed % templates.length];
}

function entitySeed(workflowId) {
  let h = 0;
  for (let i = 0; i < Math.min(workflowId.length, 8); i++) {
    h = (h * 31 + workflowId.charCodeAt(i)) & 0xffffffff;
  }
  return Math.abs(h);
}

/**
 * Generate up to `maxInsights` pastoral insights from current workflow state.
 * @param {FollowupItem[]} [followupItems]
 * @param {number} [maxInsights=2]
 * @returns {PastoralInsight[]}
 */
export function generateProactiveInsights(followupItems = null, maxInsights = 2) {
  const items   = followupItems ?? computeFollowupItems();
  const results = [];

  for (const item of items) {
    if (results.length >= maxInsights) break;

    let text    = null;
    let insType = "reminder";
    const seed  = entitySeed(item.workflowId);

    if (item.kind === "overdue") {
      text    = pickTemplate(OVERDUE_TEMPLATES, seed)(item.label);
      insType = "alert";
    } else if (item.kind === "stagnant" || item.kind === "no_progress") {
      text    = pickTemplate(STAGNANT_TEMPLATES, seed)(item.label);
      insType = "reminder";
    } else if (item.kind === "near_limit") {
      text    = pickTemplate(NEAR_LIMIT_TEMPLATES, seed)(item.label);
      insType = "reminder";
    }

    if (!text) continue;

    results.push({
      type:       insType,
      text,
      workflowId: item.workflowId,
      priority:   item.score >= 75 ? 3 : item.score >= 50 ? 2 : item.score >= 25 ? 1 : 0,
    });
  }

  // Fallback encouragement if no issues but active workflows exist
  if (results.length === 0) {
    const top = getPriorityWorkflows(1)[0];
    if (top) {
      const def        = getWorkflowDefinition(top.type);
      const entityName = top.related_entities?.[0]?.name;
      const label      = entityName ? `${def?.label ?? top.type} para ${entityName}` : def?.label ?? top.type;
      const seed       = entitySeed(top.id);
      results.push({
        type:       "encouragement",
        text:       pickTemplate(ENCOURAGEMENT_TEMPLATES, seed)(label),
        workflowId: top.id,
        priority:   0,
      });
    }
  }

  return results;
}

/** Returns the single highest-priority insight text, or null if nothing pending. */
export function getTopInsight() {
  return generateProactiveInsights(null, 1)[0]?.text ?? null;
}
